import React, { useState } from 'react';
import styled from 'styled-components';
import {
  IconButton,
  Paper,
  Typography,
  Button,
  Tooltip
} from '@material-ui/core';
import {
  Close,
  InsertDriveFile,
  DeleteForever,
  Delete,
  Forward,
  Edit,
  Folder
} from '@material-ui/icons';

import { formatDate } from '../../common/formatter';
import CardTitle from '../../common/card/CardTitle';
import { useUserContext } from '../../user/User.context';
import { FileDto } from './Document.interface';
import DocumentRenameDialog from './DocumentRenameDialog';
import DocumentDeleteDialog from './DocumentDeleteDialog';
import { FetchError } from '../../common/api/api';
import DocumentInfoDrawerTabPanel from './DocumentInfoDrawerTabPanel';
import { useHistory } from 'react-router-dom';
import { useTaskEntityFields } from '../workflow/Workflow.hooks';

const StyledDocumentInfoDrawer = styled(Paper)`
  display: flex;
  flex-direction: column;
  width: 320px;
  border-radius: 0;
  transition: all 300ms;
  &.closed {
    margin-right: -320px;
  }
  .info-header {
    background: #f1f3f3;
    color: #5a5a5a;
    .card-title {
      padding: 12px 13px 0 18px;
      font-size: 14px;
      font-weight: 500;
      line-height: 16px;
      button .MuiSvgIcon-root {
        margin-right: 0;
      }
      .header-label {
        flex: 1;
      }
    }
    .file-info {
      padding: 0 20px;
      .file-actions {
        display: flex;
        padding: 10px 0;
        align-items: center;
        .align-right {
          flex: 1;
          display: flex;
          justify-content: flex-end;
        }
        button,
        .btn-container {
          margin-right: 10px;
          &:last-child {
            margin-right: 0;
          }
        }
      }
    }
  }
`;

export interface DocumentInfoDrawerProps {
  open: boolean;
  document: FileDto;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onChange?: (file: FileDto) => void;
  onError?: (errorMessage: string) => void;
  onDocumentMove: (open: boolean, selectedIDs: number[]) => void;
}

const DocumentInfoDrawer: React.FC<DocumentInfoDrawerProps> = ({
  open,
  document,
  onClose,
  onChange,
  onSuccess,
  onError,
  onDocumentMove
}) => {
  const history = useHistory();
  const [renameOpen, setRenameOpen] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const { isFieldDisabled } = useTaskEntityFields(document.task, false);
  const can = useUserContext().permissions.file;

  const { isFolder } = document;
  // editing is determined by workflow state allowedFields
  const isTrash: boolean = document.name === 'Trash' && !!document.isLocked;
  const isDisabled: boolean = document.isLocked || isFieldDisabled('name');
  const isEditable: boolean =
    can.update && !isTrash && (isFolder || !isDisabled);

  const handleClose = () => {
    setRenameOpen(false);
    setDeleteOpen(false);
    onClose();
  };

  const handleRenameSuccess = (renamedFolder: FileDto) => {
    handleClose();
    onSuccess(`${document.name} renamed to ${renamedFolder.name}`);
  };

  const handleDeleteSuccess = (file: FileDto, message: string) => {
    handleClose();
    console.log('Delete Success:', file);
    onSuccess(message);
  };

  const handleError = (error: FetchError, errorMessage: string) => {
    if (onError && errorMessage) {
      handleClose();
      onError(errorMessage);
    }
  };

  return (
    <StyledDocumentInfoDrawer className={open ? 'open' : 'closed'}>
      <header className='info-header'>
        <CardTitle>
          {document.isFolder ? <Folder /> : <InsertDriveFile />}
          <span className='header-label'>
            {document.isFolder ? 'Folder' : 'File'} Info
          </span>
          <IconButton size='small' onClick={handleClose}>
            <Close />
          </IconButton>
        </CardTitle>
        {document && (
          <div className='file-info'>
            <Typography variant='h6'>{document.name}</Typography>
            <div className='modified-date'>
              {formatDate(document?.modified)}
            </div>
            <div className='file-actions'>
              <div className='align-left'>
                <Button
                  variant='outlined'
                  onClick={() => {
                    const basePath: string = '/portal/documents/';
                    const path: string = document.isFolder
                      ? `folder/${document.id}`
                      : `file/task/${document.taskId}`;
                    history.push(basePath + path);
                  }}
                >
                  Open
                </Button>
              </div>
              <div className='align-right'>
                {/* tooltip doesn't show when btn is disabled, so wrap with span */}
                <Tooltip
                  title={
                    isEditable
                      ? 'Rename'
                      : isTrash
                      ? 'Cannot rename the Trash folder'
                      : 'Cannot rename on this status'
                  }
                >
                  <span className='btn-container'>
                    <IconButton
                      size='small'
                      disabled={!isEditable}
                      onClick={() => setRenameOpen(true)}
                    >
                      <Edit />
                    </IconButton>
                  </span>
                </Tooltip>
                {/* add span to prevent tooltip warning if disabled */}
                <Tooltip title='Move'>
                  <span className='btn-container'>
                    <IconButton
                      size='small'
                      disabled={can.not.update || document.isLocked}
                      onClick={() => onDocumentMove(true, [document.id])}
                    >
                      <Forward />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    // also show the same tooltip for locked items
                    isTrash
                      ? 'Cannot delete the Trash folder'
                      : can.delete || document.isLocked
                      ? 'Delete'
                      : `Not authorized to delete this ${
                          document.isFolder ? 'folder' : 'file'
                        }`
                  }
                >
                  <span className='btn-container'>
                    <IconButton
                      size='small'
                      onClick={() => setDeleteOpen(true)}
                      disabled={can.not.delete || document.isLocked}
                    >
                      {document.path?.startsWith('Trash/') ? (
                        <DeleteForever />
                      ) : (
                        <Delete />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>
          </div>
        )}
      </header>
      <DocumentInfoDrawerTabPanel
        document={document}
        onChange={onChange}
        onError={error => handleError(error, error.message)}
      />
      <DocumentRenameDialog
        file={document}
        open={renameOpen}
        onCancel={() => setRenameOpen(false)}
        onSuccess={handleRenameSuccess}
        onError={handleError}
      />
      <DocumentDeleteDialog
        document={document}
        open={deleteOpen}
        onCancel={() => setDeleteOpen(false)}
        onSuccess={handleDeleteSuccess}
        onError={handleError}
      />
    </StyledDocumentInfoDrawer>
  );
};

export default DocumentInfoDrawer;
